import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import SEO from "../components/SEO";
import AuthShell from "../components/AuthShell";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import PasswordInput from "../components/PasswordInput";

const LOGIN_HERO = "https://static.prod-images.emergentagent.com/jobs/8e4196d6-aa64-413e-8d09-93aaa7923fd4/images/c5d02761dc4e539d851802868d8d518bf3c6aa6f7de88a58245b6e8878e50038.png";

export default function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const u = await login(email, password);
            toast.success("Welcome back!");
            const dest = loc.state?.from || (u.role === "admin" ? "/admin" : u.role === "coach" ? "/coach" : u.role === "staff" ? "/staff" : "/app");
            nav(dest);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <SEO title="Login — Energize.fit" path="/login" noindex />
            <AuthShell
                testid="login-shell"
                heroImage={LOGIN_HERO}
                heroQuote="Energy is not just about moving more. It’s about recovering better."
                heroAttrib="— Shahid Khan, Founder"
                heading="Welcome back."
                sub="Log in to continue your adaptive wellness journey."
            >
                <form onSubmit={submit} className="space-y-5" data-testid="login-form">
                    <div>
                        <label className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500 mb-2 block">Email</label>
                        <input
                            type="email"
                            required
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid="login-email"
                            className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500 outline-none transition"
                        />
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Password</label>
                            <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs text-stone-500 hover:text-teal-700 underline underline-offset-4">Forgot?</Link>
                        </div>
                        <PasswordInput
                            value={password}
                            onChange={setPassword}
                            required
                            autoComplete="current-password"
                            testId="login-password"
                            className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500 outline-none transition"
                        />
                    </div>
                    <button type="submit" disabled={loading} data-testid="login-submit" className="w-full inline-flex items-center justify-center gap-2 bg-stone-900 hover:bg-stone-800 text-white rounded-full px-6 py-4 font-medium hover:-translate-y-0.5 transition-all disabled:opacity-60">
                        {loading ? "Logging in…" : <>Log in <ArrowRight className="w-4 h-4" /></>}
                    </button>
                </form>
                <p className="text-sm text-stone-500 mt-8">
                    New to Energize.fit? <Link to="/signup" data-testid="login-signup-link" className="text-stone-900 font-medium underline underline-offset-4">Start your 7-day free trial</Link>
                </p>
            </AuthShell>
        </>
    );
}
